import type {
  ApplyPlanInput,
  CheckInDto,
  CheckInStatus,
  GoalCreateInput,
  GoalDto,
  GoalPlanRequest,
  PlanProposal,
  Profile,
  ChatMessageDto,
  ChatTurnPending,
  HabitDto,
  HabitStatsDto,
  HabitInput,
  LoginInput,
  MoveTasksInput,
  TaskDto,
  TaskInput,
  UserDto,
  WeekReviewDto,
} from "@habitos/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? "Error de conexión");
  return data as T;
}

type TurnResult = ChatMessageDto | ChatTurnPending;

/**
 * An assistant turn can take minutes (a long pasted plan). The server answers 202 with how
 * the turn is going when it isn't done yet; each poll waits on the server until the turn
 * ends or its progress changes, so this loop is a few long requests, not a busy one.
 */
async function followTurn(first: TurnResult, onProgress?: (turn: ChatTurnPending) => void): Promise<ChatMessageDto> {
  let result = first;
  for (let failures = 0; "pending" in result; ) {
    onProgress?.(result);
    try {
      result = await request<TurnResult>("GET", `/api/chat/turns/${result.pending}`);
      failures = 0;
    } catch (e) {
      // A dropped connection (phone asleep, network switch) or a proxy hiccup is retried;
      // any other answer from the server is final.
      const retry = !(e instanceof ApiError) || e.status >= 502;
      if (!retry || ++failures > 5) throw e;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
  return result;
}

export interface RegisterBody {
  name: string;
  email: string;
  password: string;
  timezone: string;
  inviteCode?: string;
}

export const api = {
  me: () => request<UserDto>("GET", "/api/me"),
  updateMe: (body: Partial<Pick<UserDto, "name" | "timezone" | "morningSummary" | "eveningWrapUp">>) => request<UserDto>("PATCH", "/api/me", body),
  login: (body: LoginInput) => request<UserDto>("POST", "/api/auth/login", body),
  register: (body: RegisterBody) => request<UserDto>("POST", "/api/auth/register", body),
  logout: () => request<{ ok: true }>("POST", "/api/auth/logout"),

  habits: (includeArchived = false) =>
    request<HabitDto[]>("GET", `/api/habits${includeArchived ? "?includeArchived=true" : ""}`),
  createHabit: (body: HabitInput) => request<HabitDto>("POST", "/api/habits", body),
  updateHabit: (id: string, body: HabitInput) => request<HabitDto>("PUT", `/api/habits/${id}`, body),
  archiveHabit: (id: string, archived: boolean) =>
    request<HabitDto>("POST", `/api/habits/${id}/archive`, { archived }),
  deleteHabit: (id: string) => request<void>("DELETE", `/api/habits/${id}`),

  checkIns: (from: string, to: string) => request<CheckInDto[]>("GET", `/api/checkins?from=${from}&to=${to}`),
  setCheckIn: (habitId: string, date: string, status: CheckInStatus | null) =>
    request<unknown>("PUT", "/api/checkins", { habitId, date, status }),

  tasks: (from: string, to: string) => request<TaskDto[]>("GET", `/api/tasks?from=${from}&to=${to}`),
  task: (id: string) => request<TaskDto>("GET", `/api/tasks/${id}`),
  createTask: (body: TaskInput) => request<TaskDto>("POST", "/api/tasks", body),
  updateTask: (id: string, body: TaskInput) => request<TaskDto>("PUT", `/api/tasks/${id}`, body),
  setTaskDone: (id: string, done: boolean) => request<TaskDto>("PUT", `/api/tasks/${id}/done`, { done }),
  deleteTask: (id: string) => request<void>("DELETE", `/api/tasks/${id}`),
  moveTasks: (body: MoveTasksInput) => request<TaskDto[]>("POST", "/api/tasks/move", body),

  pushConfig: () => request<{ enabled: boolean; publicKey: string | null }>("GET", "/api/push/config"),
  pushSubscribe: (sub: PushSubscriptionJSON) => request<unknown>("POST", "/api/push/subscribe", sub),
  pushUnsubscribe: (endpoint: string) => request<unknown>("POST", "/api/push/unsubscribe", { endpoint }),
  pushTest: () => request<{ delivered: number }>("POST", "/api/push/test"),

  profile: () => request<Profile>("GET", "/api/me/profile"),
  saveProfile: (body: Profile) => request<UserDto>("PUT", "/api/me/profile", body),

  chat: () => request<ChatMessageDto[]>("GET", "/api/chat"),
  sendChat: async (text: string, onProgress?: (turn: ChatTurnPending) => void) =>
    followTurn(await request<TurnResult>("POST", "/api/chat", { text }), onProgress),
  /** The turn still running, if the chat was left mid-answer. */
  currentChatTurn: () => request<ChatTurnPending | undefined>("GET", "/api/chat/turn"),
  resumeChat: (turn: ChatTurnPending, onProgress?: (turn: ChatTurnPending) => void) => followTurn(turn, onProgress),
  acceptChatPlan: (id: string, habits: HabitInput[]) =>
    request<GoalDto>("POST", `/api/chat/${id}/accept`, { habits }),
  acceptChatEdits: (id: string, indexes: number[]) =>
    request<{ applied: number }>("POST", `/api/chat/${id}/accept-edits`, { indexes }),
  acceptChatTasks: (id: string, tasks: TaskInput[]) =>
    request<TaskDto[]>("POST", `/api/chat/${id}/accept-tasks`, { tasks }),
  /** Starts the review; a slow one goes on in the chat, which picks up the running turn. */
  startReview: () => request<TurnResult>("POST", "/api/chat/review"),
  habitStats: () => request<HabitStatsDto[]>("GET", "/api/stats/habits"),
  weekReview: () => request<WeekReviewDto>("GET", "/api/review"),
  clearChat: () => request<void>("DELETE", "/api/chat"),

  aiStatus: () => request<{ enabled: boolean }>("GET", "/api/ai/status"),
  goals: () => request<GoalDto[]>("GET", "/api/goals"),
  proposePlan: (body: GoalPlanRequest) => request<PlanProposal>("POST", "/api/goals/plan", body),
  createGoal: (body: GoalCreateInput) => request<GoalDto>("POST", "/api/goals", body),
  proposeReplan: (id: string, note: string | null) =>
    request<PlanProposal>("POST", `/api/goals/${id}/replan`, { note }),
  applyPlan: (id: string, body: ApplyPlanInput) => request<GoalDto>("PUT", `/api/goals/${id}/plan`, body),
  archiveGoal: (id: string, archived: boolean) => request<GoalDto>("POST", `/api/goals/${id}/archive`, { archived }),
};
